function formatMoney(cents) {
  return Shopify.formatMoney(cents, wakaTheme.moneyFormat);
}

// The same section can be rendered more than once on a page (e.g. once on the
// dashboard, again inside a reorder trigger's dialog-modal — see
// snippets/dashboard-reorder-center.liquid's doc comment), so ids/names built
// server-side may repeat. Everything below locates its controls structurally,
// scoped to `this`, rather than by id/name — this counter only disambiguates
// the option <select> ids/names this component itself generates client-side.
let instanceCounter = 0;

// The product image URL arrives from the reorder API as a full-size Shopify CDN
// URL, so it has none of the sizing an `image_url` filter would have applied.
// The image renders at most 300px wide (see .reorder-center__image img), which
// makes the raw file several times larger than it needs to be. Shopify's CDN
// resizes on the `width` query param, so build a small srcset from it. Returns
// null for non-CDN or unparseable URLs, where the raw src is used as-is.
//
// Capped at 1x on purpose: retina rungs (450/600) get flagged by image-size
// audits, which measure against the CSS box and ignore device pixel ratio.
// Add them back here if the thumbnail looks too soft on high-density screens.
const REORDER_IMAGE_WIDTHS = [300];

function cdnImageUrl(rawUrl, width) {
  try {
    const url = new URL(rawUrl, window.location.href);
    if (!/(^|\.)cdn\.shopify\.com$/.test(url.hostname)) return null;
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return null;
  }
}

class DashboardReorderCenter extends HTMLElement {
  connectedCallback() {
    this._instanceId = instanceCounter++;
    this._config = this._readConfig();
    this._productOptions = [];
    this._productVariants = [];
    this._eyePriceCents = { right: 0, left: 0 };
    this._eyeVariantId = { right: null, left: null };
    this._eyeEnabled = { right: true, left: true };
    this._eyeSelects = { right: [], left: [] };
    this._isBundle = false;
    this._bundleBoxQty = 1;
    this._bundlePowerOptions = [];
    this._bindVariantPicker();
    this._bindCta();
    this._fetchSummary();
  }

  _readConfig() {
    const script = this.querySelector('[data-config-json]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return {};
    }
  }

  // Structural lookups scoped to `this`, not id-based — ids can repeat when
  // this component is rendered more than once on the same page.
  _qtyInput(eye) {
    const qtyRow = this.querySelector('[data-stat="qty-row"]');
    const control = qtyRow && qtyRow.querySelector(`[data-eye-control="${eye}"]`);
    return control ? control.querySelector('.input-qty__input') : null;
  }

  _eyeCheckbox(eye) {
    return this.querySelector(`[data-eye-control="${eye}"] input[type="checkbox"]`);
  }

  _bindVariantPicker() {
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (target.matches('input[type="checkbox"]')) {
        const control = target.closest('[data-eye-control]');
        if (!control) return;
        const eye = control.dataset.eyeControl;
        this._eyeEnabled[eye] = target.checked;
        this._syncEyeControls(eye);
        this._updateQtyMergeState();
        this._updateBundleProgress();
        this._updateCtaTotal();
        return;
      }

      if (target.matches('select.form-field__select')) {
        const control = target.closest('[data-eye-control]');
        if (!control) return;
        this._recomputeEyeVariant(control.dataset.eyeControl);
        return;
      }

      if (target.matches('.input-qty__input')) {
        this._updateQtyMergeState();
        this._updateBundleProgress();
        this._updateCtaTotal();
      }
    });
  }

  _fetchSummary() {
    const endpoint = this.dataset.endpoint || '/apps/lensza/reorder-summary';

    fetch(endpoint)
      .then((r) => r.json())
      .then((data) => this._render(data))
      .catch(() => {
        // Fetch failed — swap the skeleton for the server-rendered fallback values.
        this._showMainContent();
      });
  }

  _render(data) {
    if (!data || !data.has_profile) {
      this._showEmptyState();
      return;
    }

    this._isBundle = Boolean(data.is_bundle);
    this._bundleBoxQty = data.bundle_box_qty || 1;
    this._bundlePowerOptions = Array.isArray(data.bundle_power_options)
      ? data.bundle_power_options
      : [];

    this._showMainContent();
    this._renderProduct(data);
    this._renderReviews(data);
    this._renderVariantPicker(data);
    this._renderRefill(data);
    this._renderEyeQty(data);
    this._renderAddress(data);
  }

  _hideLoadingState() {
    const loadingEl = this.querySelector('[data-loading-state]');
    if (loadingEl) loadingEl.style.display = 'none';
  }

  // The empty-state copy is server-rendered from block settings (see
  // snippets/dashboard-reorder-center.liquid), so this only needs to toggle
  // visibility.
  _showEmptyState() {
    this._hideLoadingState();
    const emptyEl = this.querySelector('[data-empty-state]');
    const mainEl = this.querySelector('[data-main-content]');
    if (mainEl) mainEl.style.display = 'none';
    if (emptyEl) emptyEl.style.display = '';
  }

  _showMainContent() {
    this._hideLoadingState();
    const emptyEl = this.querySelector('[data-empty-state]');
    const mainEl = this.querySelector('[data-main-content]');
    if (emptyEl) emptyEl.style.display = 'none';
    if (mainEl) mainEl.style.display = '';
  }

  _renderProduct(data) {
    const titleEl = this.querySelector('[data-stat="product-title"]');
    if (titleEl && data.product_title) titleEl.textContent = data.product_title;

    const brandEl = this.querySelector('[data-stat="product-brand"]');
    if (brandEl && data.brand) brandEl.textContent = data.brand;

    const imageEl = this.querySelector('[data-stat="product-image"]');
    if (imageEl && data.product_image_url) {
      imageEl.innerHTML = '';
      const img = document.createElement('img');
      const candidates = REORDER_IMAGE_WIDTHS.map((width) => {
        const url = cdnImageUrl(data.product_image_url, width);
        return url ? `${url} ${width}w` : null;
      }).filter(Boolean);

      if (candidates.length === REORDER_IMAGE_WIDTHS.length) {
        img.srcset = candidates.join(', ');
        img.sizes = '300px';
        img.src = cdnImageUrl(data.product_image_url, REORDER_IMAGE_WIDTHS[0]);
      } else {
        img.src = data.product_image_url;
      }

      img.alt = data.product_title || '';
      img.decoding = 'async';
      imageEl.appendChild(img);
    }

    this._renderLastOrdered(data);
  }

  _renderLastOrdered(data) {
    const badgeEl = this.querySelector('[data-stat="last-ordered"]');
    const textEl = this.querySelector('[data-stat="last-ordered-text"]');
    if (!badgeEl || !textEl) return;

    const template = this._config.lastOrderedTemplate || '';
    if (!data.last_ordered_at || !template.includes('{date}')) {
      badgeEl.style.display = 'none';
      return;
    }

    const parsed = new Date(data.last_ordered_at);
    if (Number.isNaN(parsed.getTime())) {
      badgeEl.style.display = 'none';
      return;
    }

    const formatted = new Intl.DateTimeFormat('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(parsed);
    textEl.textContent = template.replace('{date}', formatted);
    badgeEl.style.display = '';
  }

  _renderReviews(data) {
    const rowEl = this.querySelector('[data-stat="reviews"]');
    if (!rowEl) return;

    const reviews = data.reviews;
    if (!reviews || !reviews.total_reviews || !reviews.rating) {
      rowEl.style.display = 'none';
      return;
    }

    const rating = Math.max(0, Math.min(5, Math.round(reviews.rating)));
    rowEl.querySelectorAll('[data-star]').forEach((starEl, index) => {
      const filled = index < rating;
      // Same colors as snippets/review-star-rating.liquid
      starEl.classList.toggle('text-[#FDCC0D]', filled);
      starEl.classList.toggle('text-border', !filled);
    });

    const ratingEl = rowEl.querySelector('[data-stat="reviews-rating"]');
    if (ratingEl) ratingEl.textContent = reviews.rating;

    const countEl = rowEl.querySelector('[data-stat="reviews-count"]');
    if (countEl) {
      const template = this._config.reviewsCountTemplate || '%count%';
      countEl.textContent = template.replace('%count%', reviews.total_reviews);
    }

    rowEl.style.display = '';
  }

  _renderVariantPicker(data) {
    const rowsEl = this.querySelector('[data-stat="option-rows"]');
    const rightCheckbox = this._eyeCheckbox('right');
    const leftCheckbox = this._eyeCheckbox('left');
    if (rowsEl) rowsEl.innerHTML = '';
    this._eyeSelects = { right: [], left: [] };

    if (rightCheckbox) rightCheckbox.checked = Boolean(data.power_right);
    if (leftCheckbox) leftCheckbox.checked = Boolean(data.power_left);
    this._eyeEnabled.right = Boolean(data.power_right);
    this._eyeEnabled.left = Boolean(data.power_left);

    const template = this.querySelector('[data-option-row-template]');

    if (this._isBundle) {
      // Bundle SKUs have one variant (no per-power option), so the power list
      // comes from the product's bundle_power_options metafield (fetched
      // server-side), falling back to the hardcoded config ladder when unset —
      // mirrors softlens-variants.liquid's bundle mode. The option name stays
      // the literal 'Power' marker (matched by _buildOptionRow/_eyePowerValue's
      // isPowerOption check, same as real Shopify option names) — the
      // translated label is applied separately below so a locale change can't
      // silently break power detection.
      this._productOptions = [
        {
          name: 'Power',
          values: this._bundlePowerOptions.length
            ? this._bundlePowerOptions
            : (this._config.bundlePowerValues || []),
        },
      ];
      this._productVariants = data.product_variants || [];

      if (rowsEl && template) {
        const row = this._buildOptionRow(
          rowsEl,
          template,
          this._productOptions[0],
          0,
          data,
        );
        const labelEl = row && row.querySelector('[data-option-label]');
        if (labelEl) labelEl.textContent = this._config.powerLabel || 'Power';
      }

      this._syncEyeControls('right');
      this._syncEyeControls('left');

      const bundleVariant = this._productVariants[0];
      const bundleVariantId = bundleVariant
        ? bundleVariant.id
        : data.right_variant_id || null;
      const bundlePriceCents = bundleVariant
        ? bundleVariant.price_cents || 0
        : data.right_price_cents || 0;

      this._eyeVariantId.right = bundleVariantId;
      this._eyeVariantId.left = bundleVariantId;
      this._eyePriceCents.right = bundlePriceCents;
      // Priced once as a single bundle line — see _updateCtaTotal/_collectBundleCartItem.
      this._eyePriceCents.left = 0;

      this._updateQtyMergeState();
      this._updateCtaTotal();
      return;
    }

    this._productOptions = data.product_options || [];
    this._productVariants = data.product_variants || [];

    if (this._productOptions.length && rowsEl) {
      this._productOptions.forEach((option, index) => {
        this._buildOptionRow(rowsEl, template, option, index, data);
      });
    }

    this._syncEyeControls('right');
    this._syncEyeControls('left');

    if (this._productOptions.length) {
      this._recomputeEyeVariant('right');
      this._recomputeEyeVariant('left');
    } else {
      this._eyePriceCents.right = data.right_price_cents || 0;
      this._eyePriceCents.left = data.left_price_cents || 0;
      this._eyeVariantId.right = data.right_variant_id || null;
      this._eyeVariantId.left = data.left_variant_id || null;
      this._updateCtaTotal();
    }

    this._updateQtyMergeState();
  }

  _buildOptionRow(rowsEl, template, option, index, data) {
    if (!template || !template.content) return null;
    const row = template.content.firstElementChild.cloneNode(true);
    // Append before populating <option>s: <input-select> only sets up
    // this.select/this.trigger/this.dropdown in connectedCallback, so
    // buildOptions() would throw on a still-detached clone.
    rowsEl.appendChild(row);

    const labelEl = row.querySelector('[data-option-label]');
    if (labelEl) labelEl.textContent = option.name;

    const isPowerOption = (option.name || '').toLowerCase().includes('power');

    ['right', 'left'].forEach((eye) => {
      const control = row.querySelector(`[data-eye-control="${eye}"]`);
      const inputSelectEl = control && control.querySelector('input-select');
      const selectEl = inputSelectEl && inputSelectEl.querySelector('select');
      if (!inputSelectEl || !selectEl) return;

      selectEl.id = `ReorderOption-${eye}-${index}-${this._instanceId}`;
      selectEl.name = `reorder_option_${eye}_${index}_${this._instanceId}`;

      (option.values || []).forEach((value) => {
        const optionEl = document.createElement('option');
        optionEl.value = value;
        optionEl.textContent = value;
        selectEl.appendChild(optionEl);
      });

      if (isPowerOption) {
        const powerValue = eye === 'right' ? data.power_right : data.power_left;
        if (powerValue && (option.values || []).includes(powerValue)) {
          selectEl.value = powerValue;
        }
      }

      if (typeof inputSelectEl.buildOptions === 'function')
        inputSelectEl.buildOptions();

      this._eyeSelects[eye].push(selectEl);
    });

    return row;
  }

  _syncEyeControls(eye) {
    const enabled = this._eyeEnabled[eye];
    this.querySelectorAll(`[data-eye-control="${eye}"]`).forEach((control) => {
      control.classList.toggle('opacity-60', !enabled);
      control.querySelectorAll('select').forEach((select) => {
        select.disabled = !enabled;
      });
      control.querySelectorAll('.input-select__trigger').forEach((trigger) => {
        trigger.disabled = !enabled;
        trigger.setAttribute('aria-disabled', String(!enabled));
      });
      control.querySelectorAll('.input-qty__input').forEach((input) => {
        input.disabled = !enabled;
      });
      control.querySelectorAll('.input-qty__btn').forEach((button) => {
        button.disabled = !enabled;
      });
    });
  }

  _recomputeEyeVariant(eye) {
    if (this._isBundle) {
      // Bundle SKU has a single variant — nothing to re-match on a select change.
      this._updateQtyMergeState();
      this._updateBundleProgress();
      this._updateCtaTotal();
      return;
    }

    const selects = this._eyeSelects[eye] || [];
    const selectedValues = selects.map((s) => s.value);
    const variant = this._productVariants.find(
      (v) =>
        Array.isArray(v.options) &&
        selectedValues.every((val, i) => v.options[i] === val),
    );
    this._eyePriceCents[eye] = variant ? variant.price_cents || 0 : 0;
    this._eyeVariantId[eye] = variant ? variant.id : null;
    this._updateQtyMergeState();
    this._updateBundleProgress();
    this._updateCtaTotal();
  }

  _renderRefill(data) {
    const daysEl = this.querySelector('[data-stat="days-until-empty"]');
    const barEl = this.querySelector('[data-stat="days-progress-bar"]');

    if (!data.predicted_refill_date) {
      if (daysEl) daysEl.textContent = this._config.daysPlaceholder || '—';
      if (barEl) barEl.style.width = '0%';
      return;
    }

    const days = Math.max(
      0,
      Math.ceil((new Date(data.predicted_refill_date) - Date.now()) / 86400000),
    );
    if (daysEl)
      daysEl.textContent =
        `${days} ${this._config.daysSuffixWord || ''}`.trim();

    if (barEl) {
      if (data.predicted_days_supply && data.predicted_days_supply > 0) {
        // Bar shows the REMAINING supply (full right after ordering, draining
        // toward empty as the refill date approaches) — not the elapsed share,
        // which would render an empty bar the day the customer reorders.
        const percent = (days / data.predicted_days_supply) * 100;
        // Force a reflow before setting the final width so the transition animates from
        // the initial 0% instead of snapping instantly.
        void barEl.offsetWidth;
        barEl.style.width = `${Math.max(0, Math.min(100, percent))}%`;
      } else {
        barEl.style.width = '0%';
      }
    }
  }

  _renderEyeQty(data) {
    const rightInput = this._qtyInput('right');
    const leftInput = this._qtyInput('left');
    if (rightInput) rightInput.value = data.qty_right || 1;
    if (leftInput) leftInput.value = data.qty_left || data.qty_right || 1;
    this._applyQtyMax();
    this._updateQtyMergeState();
    this._updateBundleProgress();
  }

  _applyQtyMax() {
    ['right', 'left'].forEach((eye) => {
      const input = this._qtyInput(eye);
      if (!input) return;
      if (this._isBundle) {
        input.setAttribute('max', String(this._bundleBoxQty));
      } else {
        input.removeAttribute('max');
      }
    });
  }

  // Collapses right/left into a single shared control when both eyes resolve
  // to the same variant — mirrors product-form.js's updateSoftlensSameVariantState.
  // Bundle products never merge (each eye's qty is a slice of the bundle's total).
  _updateQtyMergeState() {
    const qtyRow = this.querySelector('[data-stat="qty-row"]');
    if (!qtyRow) return;

    const rightControl = qtyRow.querySelector('[data-eye-control="right"]');
    const leftControl = qtyRow.querySelector('[data-eye-control="left"]');
    if (!rightControl || !leftControl) return;

    const isShared =
      !this._isBundle &&
      this._eyeEnabled.right &&
      this._eyeEnabled.left &&
      this._eyeVariantId.right != null &&
      this._eyeVariantId.right === this._eyeVariantId.left;

    leftControl.classList.toggle('hidden', isShared);
    rightControl.classList.toggle('col-span-2', isShared);
    rightControl.classList.toggle('flex', isShared);
    rightControl.classList.toggle('justify-center', isShared);

    const rightInput = this._qtyInput('right');
    const leftInput = this._qtyInput('left');
    if (!leftInput) return;

    if (isShared) {
      leftInput.disabled = true;
      if (rightInput) leftInput.value = rightInput.value;
      return;
    }

    leftInput.disabled = !this._eyeEnabled.left;
  }

  // Bundle products must split bundle_box_qty across the two eyes exactly —
  // mirrors product-form.js's updateSoftlensBundleState.
  _updateBundleProgress() {
    const progressEl = this.querySelector('[data-stat="bundle-progress"]');
    if (!progressEl) return;

    if (!this._isBundle) {
      progressEl.classList.add('hidden');
      progressEl.textContent = '';
      return;
    }

    progressEl.classList.remove('hidden');

    let total = 0;
    ['right', 'left'].forEach((eye) => {
      if (!this._eyeEnabled[eye]) return;
      const input = this._qtyInput(eye);
      const qty = Number.parseInt(input?.value || '0', 10);
      if (Number.isFinite(qty)) total += qty;
    });

    const isValid = total === this._bundleBoxQty;
    const label = this._config.bundleProgressLabel || '';
    progressEl.textContent = `${label}: ${total} / ${this._bundleBoxQty}`;
    progressEl.classList.toggle('text-error', !isValid);
    progressEl.classList.toggle('text-text-muted', isValid);
  }

  _updateCtaTotal() {
    const totalEl = this.querySelector('[data-stat="cta-total"]');
    if (!totalEl) return;

    if (this._isBundle) {
      totalEl.textContent = formatMoney(this._eyePriceCents.right || 0);
      return;
    }

    const right = this._eyeEnabled.right ? this._eyePriceCents.right || 0 : 0;
    const left = this._eyeEnabled.left ? this._eyePriceCents.left || 0 : 0;
    if (!right && !left) return;

    const rightInput = this._qtyInput('right');
    const rightQty = rightInput
      ? Math.max(1, Number.parseInt(rightInput.value, 10) || 1)
      : 1;
    const leftInput = this._qtyInput('left');
    const leftQty = leftInput
      ? Math.max(1, Number.parseInt(leftInput.value, 10) || 1)
      : 1;

    // Both eyes on the same variant share a single line item at the right
    // eye's quantity — see _collectCartItems — so price it just once.
    const isShared =
      right && left && this._eyeVariantId.right === this._eyeVariantId.left;

    const totalCents = isShared
      ? rightQty * right
      : (this._eyeEnabled.right ? rightQty * right : 0) +
        (this._eyeEnabled.left ? leftQty * left : 0);

    totalEl.textContent = formatMoney(totalCents);
  }

  _bindCta() {
    const ctaEl = this.querySelector('[data-stat="reorder-cta"]');
    if (ctaEl)
      ctaEl.addEventListener('click', (event) =>
        this._handleReorderClick(event),
      );
  }

  async _handleReorderClick(event) {
    const ctaEl = event.currentTarget;
    let items;
    try {
      items = this._collectCartItems();
    } catch (err) {
      this._setCtaError(err.message || this._config.addToCartErrorMessage || '');
      return;
    }

    if (!items.length) {
      this._setCtaError(this._config.noEyeSelectedError || '');
      return;
    }

    this._setCtaError('');
    window.LenszaAnalytics?.publish('reorder_started', {
      item_count: items.length,
    });
    this._setCtaLoading(ctaEl, true);

    try {
      await this._clearCart();
      for (const item of items) {
        await this._addToCart(item);
      }
      window.LenszaAnalytics?.publish('reorder_checkout_started', {
        item_count: items.length,
      });
      window.location.href = '/checkout';
    } catch (err) {
      this._setCtaError(this._config.addToCartErrorMessage || '');
      this._setCtaLoading(ctaEl, false);
    }
  }

  _eyePowerValue(eye) {
    const index = this._productOptions.findIndex((option) =>
      (option.name || '').toLowerCase().includes('power'),
    );
    if (index === -1) return null;
    const select = (this._eyeSelects[eye] || [])[index];
    return select ? select.value : null;
  }

  // Mirrors product-form.js's collectSoftlensItems line-item format: one line
  // carrying both eyes' properties when they resolve to the same variant,
  // otherwise one line per eye at its own quantity/power properties.
  _collectCartItems() {
    if (this._isBundle) return this._collectBundleCartItem();

    const rightInput = this._qtyInput('right');
    const rightQty = rightInput
      ? Math.max(1, Number.parseInt(rightInput.value, 10) || 1)
      : 1;
    const leftInput = this._qtyInput('left');
    const leftQty = leftInput
      ? Math.max(1, Number.parseInt(leftInput.value, 10) || 1)
      : 1;

    const isShared =
      this._eyeEnabled.right &&
      this._eyeEnabled.left &&
      this._eyeVariantId.right != null &&
      this._eyeVariantId.right === this._eyeVariantId.left;

    if (isShared) {
      const properties = { Eye: 'Left and Right' };
      properties._left_eye_qty = String(rightQty);
      const leftPower = this._eyePowerValue('left');
      if (leftPower !== null) properties._left_eye_power = leftPower;
      properties._right_eye_qty = String(rightQty);
      const rightPower = this._eyePowerValue('right');
      if (rightPower !== null) properties._right_eye_power = rightPower;

      return [
        { id: this._eyeVariantId.right, quantity: rightQty, properties },
      ];
    }

    const eyes = ['right', 'left'].filter(
      (eye) => this._eyeEnabled[eye] && this._eyeVariantId[eye],
    );
    if (!eyes.length) return [];

    return eyes.map((eye) => {
      const qty = eye === 'right' ? rightQty : leftQty;
      const properties = { [`_${eye}_eye_qty`]: String(qty) };
      const power = this._eyePowerValue(eye);
      if (power !== null) properties[`_${eye}_eye_power`] = power;
      if (eyes.length > 1) {
        properties.Eye = eye === 'right' ? 'Right' : 'Left';
      }
      return { id: this._eyeVariantId[eye], quantity: qty, properties };
    });
  }

  // Mirrors product-form.js's collectSoftlensBundleItem: a bundle SKU is a
  // single fixed-price unit whose contents are split across the two eyes, so
  // it always adds one line at quantity 1 carrying both eyes' human-readable
  // summary + machine properties, and the split must sum to bundle_box_qty.
  _collectBundleCartItem() {
    const eyes = ['right', 'left'].filter((eye) => this._eyeEnabled[eye]);
    if (!eyes.length) return [];

    const properties = {};
    let total = 0;

    eyes.forEach((eye) => {
      const input = this._qtyInput(eye);
      const qty = Number.parseInt(input?.value || '0', 10);
      if (!Number.isFinite(qty) || qty <= 0) return;

      const power = this._eyePowerValue(eye);
      const boxLabel =
        qty === 1
          ? this._config.boxSingularLabel || 'Box'
          : this._config.boxPluralLabel || 'Boxes';
      const powerLabel = this._config.powerLabel || 'Power';
      const summary = power
        ? `${powerLabel} ${power} - ${qty} ${boxLabel}`
        : `${qty} ${boxLabel}`;

      properties[eye === 'right' ? 'Right Eye' : 'Left Eye'] = summary;
      if (power !== null) properties[`_${eye}_eye_power`] = power;
      properties[`_${eye}_eye_qty`] = String(qty);

      total += qty;
    });

    if (!total) return [];

    if (total !== this._bundleBoxQty) {
      const template = this._config.bundleQtyErrorTemplate || '';
      throw new Error(template.replace('{qty}', String(this._bundleBoxQty)));
    }

    const variantId = this._eyeVariantId.right;
    if (!variantId) return [];

    return [{ id: variantId, quantity: 1, properties }];
  }

  async _clearCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('cart_clear_failed');
  }

  async _addToCart(item) {
    const formData = new FormData();
    formData.append('id', String(item.id));
    formData.append('quantity', String(item.quantity));
    if (item.properties) {
      Object.entries(item.properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
    }

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });
    if (!response.ok) throw new Error('cart_add_failed');
  }

  _setCtaLoading(ctaEl, isLoading) {
    ctaEl.classList.toggle('loading', isLoading);
    ctaEl.disabled = isLoading;
  }

  _setCtaError(message) {
    const el = this.querySelector('[data-stat="reorder-error"]');
    if (!el) return;
    el.textContent = message || '';
    el.classList.toggle('hidden', !message);
  }

  _renderAddress(data) {
    const el = this.querySelector('[data-stat="address-full"]');
    if (!el || !data.preferred_address) return;

    const { address1, address2, city, province, country, postal_code } =
      data.preferred_address;
    const cityProvince = [city, province].filter(Boolean).join(', ');
    const cityLine = [cityProvince, postal_code].filter(Boolean).join(' ');

    const lines = [address1, address2, cityLine, country].filter(Boolean);
    if (!lines.length) return;

    el.innerHTML = '';
    lines.forEach((line) => {
      const lineEl = document.createElement('div');
      lineEl.textContent = line;
      el.appendChild(lineEl);
    });
  }
}

if (!customElements.get('dashboard-reorder-center')) {
  customElements.define('dashboard-reorder-center', DashboardReorderCenter);
}
